import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { THEMES } from '../lib/themes';
import { applyTheme } from '../lib/ThemeProvider';

export default function ThemesTab() {
  const [activeTheme, setActiveTheme] = useState('navy');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    supabase.from('settings').select('value').eq('key','theme').single()
      .then(({ data }) => { if (data?.value) setActiveTheme(data.value); })
      .catch(() => {});
  }, []);

  const apply = async (themeKey) => {
    setSaving(true); setSaved('');
    await supabase.from('settings').upsert({ key: 'theme', value: themeKey, updated_at: new Date().toISOString() });
    setActiveTheme(themeKey);
    applyTheme(themeKey); // Apply immediately in admin too
    setSaving(false);
    setSaved(themeKey);
    setTimeout(() => setSaved(''), 3000);
  };

  return (
    <div>
      {/* Info */}
      <div style={{ background:'rgba(184,134,11,0.08)', border:'1px solid #d4b896', borderRadius:10, padding:'12px 16px', marginBottom:24, fontSize:'0.82rem', color:'#5a4e3e', lineHeight:1.6 }}>
        🎨 <strong style={{color:'#8b6508'}}>Theme Manager:</strong> Choose a theme for your church website. Changes apply instantly for all visitors. You can change it monthly for a fresh look!
      </div>

      {saved && (
        <div style={{ background:'#f0fff0', border:'1px solid #5cb85c', borderRadius:10, padding:'12px 16px', marginBottom:20, color:'#2d7a2d', fontSize:'0.88rem' }}>
          ✅ <strong>{THEMES[saved]?.name}</strong> theme is now live on your website!
        </div>
      )}

      {/* Theme grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:16 }}>
        {Object.entries(THEMES).map(([key, theme]) => {
          const isActive = activeTheme === key;
          return (
            <div key={key} style={{
              border: isActive ? `3px solid ${theme.preview[1]}` : '1.5px solid #e8d8b0',
              borderRadius:16, overflow:'hidden', background:'#fff',
              boxShadow: isActive ? `0 4px 24px ${theme.preview[1]}40` : '0 2px 8px rgba(0,0,0,0.06)',
              transition:'all 0.2s',
            }}>
              {/* Mini website preview */}
              <div style={{ background: theme.preview[0], padding:'14px 16px', position:'relative' }}>
                {isActive && (
                  <div style={{ position:'absolute', top:8, right:8, background: theme.preview[1], color: theme.preview[0], fontSize:'0.6rem', fontWeight:700, padding:'3px 9px', borderRadius:20, letterSpacing:'0.1em' }}>
                    ACTIVE
                  </div>
                )}
                {/* Fake navbar */}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                  <div style={{ width:24, height:18, borderRadius:'50%', border:`2px solid ${theme.preview[1]}`, background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'8px', color:theme.preview[1] }}>+</div>
                  <div style={{ fontSize:'8px', color:theme.preview[1], fontWeight:700, letterSpacing:'0.05em' }}>ZION AG CHURCH</div>
                </div>
                {/* Fake hero */}
                <div style={{ textAlign:'center', padding:'8px 0' }}>
                  <div style={{ fontSize:'7px', color:theme.preview[1], letterSpacing:'0.3em', marginBottom:4 }}>+ JESUS IS LORD +</div>
                  <div style={{ fontFamily:'Georgia,serif', fontSize:'18px', color:theme.preview[2], fontWeight:'bold', lineHeight:1.1, marginBottom:3 }}>
                    ZION <span style={{color:theme.preview[1]}}>AG</span>
                  </div>
                  <div style={{ fontSize:'7px', color:'rgba(255,255,255,0.5)', marginBottom:8, letterSpacing:'0.1em' }}>OOTY, TAMIL NADU</div>
                  <div style={{ display:'flex', gap:6, justifyContent:'center' }}>
                    <div style={{ background: theme.preview[1], color: theme.preview[0], fontSize:'6px', fontWeight:700, padding:'4px 10px', borderRadius:10 }}>JOIN US</div>
                    <div style={{ border:`1px solid ${theme.preview[1]}`, color:theme.preview[1], fontSize:'6px', fontWeight:700, padding:'4px 10px', borderRadius:10 }}>PRAYER</div>
                  </div>
                </div>
                {/* Fake cards row */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4, marginTop:10 }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ background:`${theme.preview[1]}18`, border:`1px solid ${theme.preview[1]}30`, borderRadius:6, padding:'6px 4px', textAlign:'center' }}>
                      <div style={{ fontSize:'7px', color:theme.preview[1], fontWeight:700, marginBottom:2 }}>{['Events','Sermons','Gallery'][i]}</div>
                      <div style={{ fontSize:'6px', color:'rgba(255,255,255,0.4)' }}>— — —</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Theme info */}
              <div style={{ padding:'14px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:'18px' }}>{theme.emoji}</span>
                  <div>
                    <div style={{ fontFamily:"'Cinzel Decorative',serif", fontSize:'0.82rem', color:'#1a2a4a', fontWeight:700 }}>{theme.name}</div>
                    <div style={{ fontSize:'0.72rem', color:'#9e8f7a', marginTop:1 }}>{theme.desc}</div>
                  </div>
                </div>
                {/* Color swatches */}
                <div style={{ display:'flex', gap:6, marginBottom:12, marginTop:8 }}>
                  {theme.preview.map((c, i) => (
                    <div key={i} style={{ width:20, height:20, borderRadius:'50%', background:c, border:'1.5px solid #e8d8b0' }} />
                  ))}
                </div>
                <button
                  onClick={() => apply(key)}
                  disabled={saving || isActive}
                  style={{
                    width:'100%', padding:'10px', borderRadius:9, border:'none',
                    background: isActive
                      ? 'linear-gradient(135deg,#5cb85c,#2d7a2d)'
                      : 'linear-gradient(135deg,#c9a84c,#8b6914)',
                    color: isActive ? '#fff' : '#070f22',
                    fontFamily:"'Raleway',sans-serif", fontWeight:700,
                    fontSize:'0.78rem', cursor: isActive ? 'default' : 'pointer',
                    letterSpacing:'0.1em', opacity: saving ? 0.7 : 1,
                  }}
                >
                  {isActive ? '✅ Currently Active' : saving ? '⏳ Applying...' : `Apply ${theme.name}`}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop:24, background:'#fff', border:'1px solid #e8d8b0', borderRadius:12, padding:'16px 20px', fontSize:'0.8rem', color:'#6b5e4e', lineHeight:1.7 }}>
        <strong style={{color:'#8b6508'}}>💡 Monthly Theme Tip:</strong> Change your theme with the seasons! Try Maroon for Christmas season, Green for summer, Purple for Easter, and Navy or Midnight for regular months.
      </div>
    </div>
  );
}
